import React from "react";
import { Box, Container, Typography, Card, CardContent } from "@mui/material";
import { Store, LocationOn } from "@mui/icons-material";
import { motion } from "framer-motion";
import { gradients } from "../../../utils/theme";
import AnimatedSection from "../AnimatedSection";

/**
 * StatBox - Reusable component for displaying statistics
 */
interface StatBoxProps {
  value: string;
  label: string;
}

const StatBox: React.FC<StatBoxProps> = ({ value, label }) => (
  <Box>
    <Typography
      variant="h3"
      sx={{
        fontWeight: 700,
        color: "white",
        mb: 0.5,
      }}
    >
      {value}
    </Typography>
    <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.9)" }}>
      {label}
    </Typography>
  </Box>
);

/**
 * Clients Section Component
 *
 * Displays an infinite carousel of trusted mall clients with stats below.
 */
const ClientsSection: React.FC = () => {
  const clients = [
    {
      name: "Skyline Plaza",
      location: "Mumbai, Maharashtra",
      since: "2023",
    },
    {
      name: "Crown Mall",
      location: "New Delhi",
      since: "2023",
    },
    {
      name: "Horizon Square",
      location: "Hyderabad, Telangana",
      since: "2024",
    },
    {
      name: "Metro Junction",
      location: "Bangalore, Karnataka",
      since: "2024",
    },
    {
      name: "Central Park Mall",
      location: "Navi Mumbai, Maharashtra",
      since: "2024",
    },
    {
      name: "Galaxy Galleria",
      location: "Pune, Maharashtra",
      since: "2023",
    },
    {
      name: "Silver Heights",
      location: "Chennai, Tamil Nadu",
      since: "2024",
    },
    {
      name: "Grand Central",
      location: "Kolkata, West Bengal",
      since: "2023",
    },
  ];

  return (
    <Box
      id="our-clients"
      sx={{
        py: { xs: 6, md: 10 },
        background: gradients.navyLux,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <AnimatedSection>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "2.5rem" },
              fontWeight: 700,
              mb: 2,
              textAlign: "center",
              color: "white",
            }}
          >
            Premium Mall Partners
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 6,
              textAlign: "center",
              color: "rgba(255,255,255,0.9)",
              fontWeight: 400,
            }}
          >
            Leading shopping destinations trust MallInHand for exceptional
            experiences
          </Typography>
        </AnimatedSection>

        {/* Carousel Container */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            mx: { xs: -2, sm: -4 },
          }}
        >
          {/* Infinite Carousel */}
          <motion.div
            animate={{
              x: [0, -2400],
            }}
            transition={{
              x: {
                repeat: Infinity,
                repeatType: "loop",
                duration: 40,
                ease: "linear",
              },
            }}
            style={{
              display: "flex",
              gap: "24px",
              paddingLeft: "24px",
            }}
          >
            {/* First Set of Clients */}
            {clients.map((client, index) => (
              <motion.div
                key={`client-${index}`}
                style={{ flexShrink: 0 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <Card
                  elevation={4}
                  sx={{
                    width: 280,
                    height: 180,
                    background: "white",
                    borderRadius: 3,
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: 8,
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 3,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    {/* Badge */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        background: gradients.gold,
                        color: "#1a237e",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        boxShadow: "0 2px 8px rgba(255,215,0,0.3)",
                      }}
                    >
                      Since {client.since}
                    </Box>

                    {/* Icon */}
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        background: gradients.gold,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                        boxShadow: "0 4px 12px rgba(255,215,0,0.2)",
                      }}
                    >
                      <Store sx={{ fontSize: 32, color: "#1a237e" }} />
                    </Box>

                    {/* Content */}
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: "text.primary",
                        }}
                      >
                        {client.name}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LocationOn
                          sx={{ fontSize: 16, color: "text.secondary" }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.875rem" }}
                        >
                          {client.location}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            ))}

            {/* Duplicate Set for Seamless Loop */}
            {clients.map((client, index) => (
              <motion.div
                key={`client-duplicate-${index}`}
                style={{ flexShrink: 0 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <Card
                  elevation={4}
                  sx={{
                    width: 280,
                    height: 180,
                    background: "white",
                    borderRadius: 3,
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: 8,
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 3,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        background: gradients.gold,
                        color: "#1a237e",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        boxShadow: "0 2px 8px rgba(255,215,0,0.3)",
                      }}
                    >
                      Since {client.since}
                    </Box>

                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        background: gradients.gold,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                        boxShadow: "0 4px 12px rgba(255,215,0,0.2)",
                      }}
                    >
                      <Store sx={{ fontSize: 32, color: "#1a237e" }} />
                    </Box>

                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: "text.primary",
                        }}
                      >
                        {client.name}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LocationOn
                          sx={{ fontSize: 16, color: "text.secondary" }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.875rem" }}
                        >
                          {client.location}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </Box>

        {/* Stats Below Carousel */}
        <AnimatedSection delay={0.3}>
          <Box
            sx={{
              mt: 8,
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              gap: { xs: 2, sm: 3, md: 4 },
              textAlign: "center",
            }}
          >
            <StatBox value="20+" label="Shopping Malls" />
            <StatBox value="15+" label="Cities Covered" />
            <StatBox value="500K+" label="Happy Customers" />
          </Box>
        </AnimatedSection>
      </Container>
    </Box>
  );
};

export default ClientsSection;
